/**
 * O pedido na hora — spec 0071 (C-04 a C-07, FR-015, RN-03 a RN-05).
 *
 * Usado na página do telefone e na página do link único. A decisão é definitiva,
 * por isso autorizar pede um segundo toque que diz exatamente o que se autoriza;
 * recusar não pede, porque recusar é o que vale sem resposta.
 */

import React, { useState } from 'react';
import { ErroDaPagina, hora } from './familia-comum';

export interface PedidoDaFamilia {
  idPedido: number;
  crianca: string;
  tipo: 'receber_outra_pessoa' | 'passageiro_extra' | 'descer_no_caminho';
  dados: { nome?: string; documento?: string; parentesco?: string; local?: string };
  status: 'aguardando' | 'autorizado' | 'recusado' | 'expirado';
  expiraEm: string;
}

function quemRecebe(p: PedidoDaFamilia): string {
  return [p.dados.nome, p.dados.parentesco].filter(Boolean).join(', ') || 'Uma pessoa';
}

export function fraseDoPedido(p: PedidoDaFamilia): string {
  if (p.tipo === 'receber_outra_pessoa') return `${quemRecebe(p)} quer receber ${p.crianca} agora, na volta da escola.`;
  if (p.tipo === 'descer_no_caminho') return `${p.crianca} pediu para descer em ${p.dados.local ?? 'outro lugar'}, e não no ponto de sempre.`;
  return `${p.crianca} pediu para ir hoje numa van que não é a dela.`;
}

/** O que acontece se autorizar — e o que vale sem resposta (RN-05). */
function efeito(p: PedidoDaFamilia): { autorizando: string; semResposta: string } {
  if (p.tipo === 'receber_outra_pessoa') {
    return {
      autorizando: `O motorista entrega ${p.crianca} a ${p.dados.nome ?? 'essa pessoa'} hoje. Vale só para esta viagem.`,
      semResposta: `${p.crianca} continua na van.`,
    };
  }
  if (p.tipo === 'descer_no_caminho') {
    return { autorizando: `${p.crianca} desce em ${p.dados.local ?? 'outro lugar'}, só nesta viagem.`, semResposta: `${p.crianca} desce no ponto de sempre.` };
  }
  return { autorizando: `${p.crianca} embarca nesta viagem, se houver lugar na van.`, semResposta: `${p.crianca} não embarca.` };
}

const RESULTADO: Record<string, (p: PedidoDaFamilia) => string> = {
  autorizado: (p) => `Autorizado. ${efeito(p).autorizando} O motorista já pode ver a sua resposta.`,
  recusado: (p) => `Recusado. ${efeito(p).semResposta} O motorista já pode ver a sua resposta.`,
  expirado: (p) => `Este pedido expirou sem resposta, e vale a regra: ${efeito(p).semResposta}`,
};

export const DecisaoDoPedido: React.FC<{
  pedido: PedidoDaFamilia;
  decidir: (autoriza: boolean) => Promise<{ status: string }>;
  nivelTitulo?: 'h1' | 'h2';
}> = ({ pedido, decidir, nivelTitulo = 'h2' }) => {
  const [status, setStatus] = useState(pedido.status);
  const [confirmando, setConfirmando] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const Titulo = nivelTitulo;
  const e = efeito(pedido);
  // Vencido na tela antes de a página recarregar: vale a regra, sem esperar o servidor.
  const mostrado = status === 'aguardando' && Date.parse(pedido.expiraEm) <= Date.now() ? 'expirado' : status;

  const enviar = async (autoriza: boolean) => {
    setErro(null);
    setOcupado(true);
    try {
      setStatus((await decidir(autoriza)).status as PedidoDaFamilia['status']);
      setConfirmando(false);
    } catch (err) {
      if (err instanceof ErroDaPagina && err.codigo === 'JA_DECIDIDA') {
        setErro('Outro responsável já respondeu a este pedido.');
      } else {
        setErro((err as Error).message);
      }
    } finally {
      setOcupado(false);
    }
  };

  return (
    <section className="fam-pedido" aria-labelledby={`fam-pedido-${pedido.idPedido}`}>
      <Titulo id={`fam-pedido-${pedido.idPedido}`} className="fam-pedido-frase">{fraseDoPedido(pedido)}</Titulo>
      {pedido.tipo === 'receber_outra_pessoa' && pedido.dados.documento && (
        <p className="fam-pedido-apoio">Documento informado: {pedido.dados.documento}</p>
      )}

      {mostrado === 'aguardando' ? (
        <>
          <p className="fam-pedido-apoio">
            Responda até as <b>{hora(pedido.expiraEm)}</b>. Sem resposta: {e.semResposta}
          </p>
          {!confirmando ? (
            <div className="fam-pedido-acoes">
              <button type="button" className="fam-btn fam-btn-primario" onClick={() => setConfirmando(true)} disabled={ocupado}>
                Autorizar
              </button>
              <button type="button" className="fam-btn fam-btn-secundario" onClick={() => enviar(false)} disabled={ocupado}>
                {ocupado ? 'Enviando…' : 'Recusar'}
              </button>
            </div>
          ) : (
            <div className="fam-pedido-confirmar" role="group" aria-label="Confirmar a autorização">
              <p className="fam-pedido-apoio"><b>{e.autorizando}</b> Depois de confirmar, não dá para mudar.</p>
              <div className="fam-pedido-acoes">
                <button type="button" className="fam-btn fam-btn-primario" onClick={() => enviar(true)} disabled={ocupado} autoFocus>
                  {ocupado ? 'Enviando…' : 'Sim, autorizo'}
                </button>
                <button type="button" className="fam-btn fam-btn-texto" onClick={() => setConfirmando(false)} disabled={ocupado}>
                  Voltar
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <p className={mostrado === 'autorizado' ? 'fam-ok' : 'fam-aviso'} role="status">{RESULTADO[mostrado](pedido)}</p>
      )}
      {erro && <p className="fam-erro" role="alert">{erro}</p>}
    </section>
  );
};
